import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { Play } from 'lucide-react'
import { cn } from '@/lib/utils'

interface VideoFacadeProps {
    vimeoId?: string
    youtubeId?: string
    hash?: string
    title: string
    poster: string
    meta?: string
    className?: string
}

/** Shows a still with a play button and only loads the Vimeo or YouTube player when asked to. */
export const VideoFacade: FC<VideoFacadeProps> = ({ vimeoId, youtubeId, hash, title, poster, meta, className }): ReactNode => {
    const [playing, setPlaying] = useState(false)

    const params = new URLSearchParams({
        autoplay: '1',
        title: '0',
        byline: '0',
        portrait: '0',
        dnt: '1',
        playsinline: '1',
        color: 'F96529'
    })

    if (hash) params.set('h', hash)

    const src = youtubeId
        ? `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1`
        : `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`

    return (
        <div className={cn('relative aspect-video overflow-hidden bg-night', className)}>
            {playing
                ? (
                    <iframe
                        src={src}
                        title={title}
                        className='absolute inset-0 size-full'
                        allow='autoplay; fullscreen; picture-in-picture; encrypted-media'
                        allowFullScreen
                    />
                )
                : (
                    <button
                        type='button'
                        onClick={() => setPlaying(true)}
                        className='group absolute inset-0 block size-full text-left'
                        data-cursor='Spill'
                        aria-label={`Spill av video: ${title}`}
                    >
                        <img src={poster} alt='' loading='lazy' decoding='async' className='size-full object-cover transition-transform duration-[1.2s] ease-out-quint group-hover:scale-105' />
                        <span className='absolute inset-0 bg-gradient-to-t from-night/80 via-night/10 to-transparent' />
                        <span className='absolute bottom-5 left-5 flex items-center gap-4 text-paper md:bottom-8 md:left-8'>
                            <span className='flex size-16 items-center justify-center bg-signal text-night transition-transform duration-500 ease-out-quint group-hover:scale-110 md:size-20'>
                                <Play className='size-6 fill-current' aria-hidden='true' />
                            </span>
                            {meta && <span className='t-label'>{meta}</span>}
                        </span>
                    </button>
                )}
        </div>
    )
}
